import { Params } from '@angular/router';
import { CalendarChartInput } from '../shared/calendar-chart/models/calendar-chart-input.interface';
import {
  GoogleMapsDrawingsInput,
  MapTypeId,
} from '../shared/google-maps/model/google-maps-input.interface';
import { MatSymbol } from '../shared/icons/models/mat-symbol.interface';
import { TileV2 } from '../shared/tile/models/tile-v2.interface';
import { TimelineChartInputV2 } from '../shared/timeline-chart/models/timeline-chart-input.interface';
import { PeriodSelectorState } from './period-selector';

export interface Tile {
  title?: string;
  subtitle?: string;
  values?: TileValue[];
  link?: any[] | string | null;
  queryParams?: Params | null;
  chart?: Chart;
  extension?: TileExtension;
  innerMapDrawings?: GoogleMapsDrawingsInput;
}

export type TileExtension = TileValue[][];

export interface MapTile {
  routerLink?: string | string[];
  queryParams?: Params | null;
  drawings: GoogleMapsDrawingsInput;
  mapTypeId?: MapTypeId;
  title?: string;
  grayscale?: boolean;
}

export interface TileGrid {
  tiles: { [tileId: string]: TileType };
  template: string[][];
  /**
   * The period the backend actually applied. Same member, same meaning as
   * `RemoteTable.periodSelector` — the stat pickers render it when the
   * frontend sent no period. The stat family never receives
   * `mode: 'unfiltered'`.
   */
  periodSelector?: PeriodSelectorState;
}

export type TileType =
  Tile | TileV2 | MapTile | CalendarChartInput | TimelineChartInputV2;

export interface ChartData {
  value: number;
  category: string;
  color: string;
  matsymbol?: MatSymbol;
}

export interface Chart {
  id: string;
  /**
   * Optional on purpose: the back end sends `chart: {id}` with no `data` key for
   * a tile whose chart has nothing to plot (measured on the IPT subcontractor
   * daily dashboard, where `categories` and `tasks` arrive that way). Declaring
   * it non-optional hid the unguarded reads in `donut-chart` from
   * `strictTemplates` until one of them threw and truncated the tile grid.
   */
  data?: ChartData[];
}

export interface Segment {
  value: number;
  color: string;
  main: StrokeDashConfig;
  overlap?: StrokeDashConfig;
  overlapBorder?: StrokeDashConfig;
}

interface StrokeDashConfig {
  strokeDashArray: string;
  strokeDashOffset: number;
}

export interface TextValue {
  text: string;
  subText?: string;
  fontSize?: number;
  info?: Omit<TextValue, 'info'>;
}

export interface NumberValue {
  number: number;
  unit?: string;
  prefix?: string;
  info?: Omit<NumberValue, 'info'>;
}

export interface DateValue {
  date: string;
  dateFormat?: string;
}

export interface DatePeriodValue {
  date1: string;
  date2: string;
  date1Format?: string;
  date2Format?: string;
  showDay?: boolean;
}

export interface MatSymbolValue {
  symbol: string;
  rotationAngle?: number;
  color?: string;
}

export type TileValue =
  TextValue | DateValue | NumberValue | DatePeriodValue | MatSymbolValue;

export const distinctTileProps: Exclude<keyof Tile, keyof TileV2>[] = [
  'chart',
  'innerMapDrawings',
  'link',
  'queryParams',
  'values',
];

export const distinctTileV2Props: Exclude<keyof TileV2, keyof Tile>[] = [
  'innerMap',
  'nav',
  'primaryValue',
  'progressBar',
  'secondaryValues',
];
